import 'antd/dist/antd.min.css';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import { LanguageSelector } from './components/LanguageSelector';
import './fonts/AimoSans-Regular.ttf';
import { ReactComponent as AimoLogo } from './images/logo.svg';
import './index.css';
import ParkingProductSelection from './views/ParkingProductSelection';
import LoginByCredentials from './views/LoginByCredentials';
import LoginByMagicLink from './views/LoginByMagicLink';
import Registration from './views/Registration';
import Success from './views/Success';
import { KioskProvider } from './context/kioskContext';
import { PhoneFilled, WarningFilled, FileProtectOutlined } from '@ant-design/icons';
import ContactModal from './components/ContactModal';
import ErrorKioskModal from './components/ErrorKioskModal';
import { isEmpty } from 'lodash';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import ParkingLocationsSelection from './views/ParkingLocationsSelection';
import i18n from './i18n';
import { ENGLISH } from './constants/LanguageCodes';
import { isPageReloadOrRefresh, mapToLanguageCode } from './utils';

const MainContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const LogoContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
`;

const LanguageContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  @media screen and (min-width: 600px) {
    margin-left: auto;
  }
  @media screen and (max-width: 600px) {
    justify-content: space-between;
    flex: 1;
  }
  .refreshButton {
    display: none;
  }

  @media screen and (hover: none) and (min-device-aspect-ratio: 1/1) and (orientation: landscape) {
    .refreshButton {
      display: initial;
      float: right;
    }
  }
`;

const Logo = styled.svg`
    width: 200px;
`;

const PhoneIcon = styled(PhoneFilled)`
  margin-right: 30px;
  font-size: 30px;
  color:white;

  &:hover {
    color: #008939;
  }
`;

const WarningIcon = styled(WarningFilled)`
  margin-right: 30px;
  font-size: 30px;
  color:yellow;
`;

const PrivacyPolicyLogo = styled(FileProtectOutlined)`
  font-size: 30px;
  margin-right: 10px;
  color:white;
  &:hover {
    color: #008939;
  }
`;

const PrivacyPolicyContainer = styled.div`
  margin-right: 20px;
  font-size: 20px;
  color: white;

  button, button:hover, button:active, button:focus-visible {
    color: white;
  }
`;

const HeaderIconButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 3px solid #008939;
    outline-offset: 3px;
  }
`;

const IDContainer = styled.div`
  display: flex;
  text-overflow: ellipsis;
  justify-content: right;
  @media (min-width: 820px) {
    flex: 1;
  }
`;


function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [ confirmSupportModalOpen, setConfirmSupportModalOpen ] = useState(false);
  const [ confirmErrorModalOpen, setConfirmErrorModalOpen ] = useState(false);
  const [ confirmPolicyModalOpen, setConfirmPolicyModalOpen ] = useState(false);
  const [ kioskState, setKioskState ] = useState();
  const kioskUid = kioskState?.kiosk?.uid;
  const isLoginPage = window.location.pathname === '/login';

  const countryCode = kioskState?.kiosk?.countryCode?.toLowerCase();

  useEffect(() => {
    const syncDocumentLang = () => {
      const lng = i18n.resolvedLanguage || i18n.language;
      if (lng) {
        document.documentElement.lang = lng;
      }
    };
    syncDocumentLang();
    i18n.on('languageChanged', syncDocumentLang);
    return () => i18n.off('languageChanged', syncDocumentLang);
  }, [ i18n ]);

  useEffect(() => {
    const isPageReloadedOrRefreshed = isPageReloadOrRefresh();
    const isLanguageLocalStorage = localStorage.getItem('language');

    if (isPageReloadedOrRefreshed && !countryCode) {
      localStorage.removeItem('language');
    }

    if (isLoginPage && isPageReloadedOrRefreshed) {
      localStorage.removeItem('language');
      i18n.changeLanguage(ENGLISH);
    }

    if (isPageReloadedOrRefreshed && countryCode && !isLanguageLocalStorage) {
      i18n.changeLanguage(mapToLanguageCode(countryCode.toLowerCase()));
    }
  }, [ countryCode, isLoginPage, i18n ]);


  return (<MainContainer>
    <a className="skip-link" href="#main-content">{t('a11y.skipToContent')}</a>
    <LogoContainer>
      <Logo viewBox="0 0 60 10">
        <AimoLogo fill="#40A94E" width height/>
      </Logo>

      { kioskUid && location.pathname !== '/success' && <IDContainer><span>ID:&nbsp;{kioskUid}</span></IDContainer>
      }
      <LanguageContainer>
        { countryCode &&
          <PrivacyPolicyContainer>
            <HeaderIconButton
              type="button"
              aria-label={t('a11y.openPrivacyPolicy')}
              onClick={() => setConfirmPolicyModalOpen(true)}
            >
              <span aria-hidden="true"><PrivacyPolicyLogo /></span>
            </HeaderIconButton>
          </PrivacyPolicyContainer>
        }
        {!isEmpty(kioskState?.error) && (
          <HeaderIconButton
            type="button"
            aria-label={t('a11y.openKioskErrorDetails')}
            onClick={() => setConfirmErrorModalOpen(true)}
          >
            <span aria-hidden="true"><WarningIcon /></span>
          </HeaderIconButton>
        )}
        <HeaderIconButton
          type="button"
          aria-label={t('a11y.openContactInformation')}
          onClick={() => setConfirmSupportModalOpen(true)}
        >
          <span aria-hidden="true"><PhoneIcon /></span>
        </HeaderIconButton>
        <LanguageSelector kioskCountry={countryCode}/>
      </LanguageContainer>
    </LogoContainer>
    <KioskProvider>
      <main id="main-content" tabIndex={-1}>
        <Switch>
          <AuthenticatedRoute
            exact
            path="/parking-locations"
            render={props => <ParkingLocationsSelection {...props} setKioskState={setKioskState}/>}
          />
          <AuthenticatedRoute
            exact
            path="/kiosk"
            render={props => <ParkingProductSelection {...props} setKioskState={setKioskState}/>}
          />
          <AuthenticatedRoute
            path="/register"
            render={props => <Registration {...props}/>}
          />
          <AuthenticatedRoute
            path="/success"
            render={props => <Success {...props}
              setCountryCode={() => {}}
              setKioskState={setKioskState}
            />}
          />
          <Route path="/kiosk/:magicLinkId" component={LoginByMagicLink}/>
          <Route path="/parking-locations/:magicLinkId" component={LoginByMagicLink}/>
          <Route path="/login" component={LoginByCredentials}/>
          <Route path="/"><Redirect to="/login"/></Route>
        </Switch>
      </main>
    </KioskProvider>
    <ContactModal
      confirmModalOpen={confirmSupportModalOpen}
      setConfirmModalOpen={setConfirmSupportModalOpen}
      countryCode={countryCode}
      isLoginPage={isLoginPage}
    />
    {countryCode && <>
      <ErrorKioskModal
        confirmModalOpen={confirmErrorModalOpen}
        setConfirmModalOpen={setConfirmErrorModalOpen}
        kioskState={kioskState}
      />
      <PrivacyPolicyModal
        confirmModalOpen={confirmPolicyModalOpen}
        setConfirmModalOpen={setConfirmPolicyModalOpen}
        countryCode={countryCode} />
    </>}
  </MainContainer>
  );
}

App.propTypes = {};

export default App;
